<template>
  <v-row dense>
    <v-col cols="12">
      <!-- ⚠️ แท็บนี้ต่างจากแท็บอื่นในหน้านี้ — บันทึกลงฐานข้อมูลจริง ไม่ใช่ localStorage
           ต้องบอกให้ชัด ไม่งั้นคนจะเหมาว่าทุกแท็บทำงานเหมือนกัน -->
      <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-database-check-outline">
        <span class="text-body-2">
          {{ $t('qaNbaDbNote') }}
        </span>
      </v-alert>
    </v-col>

    <v-col cols="12" md="7">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaNbaPromptTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <v-textarea
            v-model="config.systemPrompt"
            variant="outlined"
            rows="14"
            hide-details
            auto-grow
            spellcheck="false"
            :placeholder="$t('qaEvalPromptPlaceholder')"
          />
          <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaNbaPromptAppendNote') }}
            </span>
          </v-alert>
          <div class="text-caption text-medium-emphasis mt-2">
            {{ $t('qaEvalPromptNote') }}
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="5">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalOperation') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaNbaEnable') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaNbaEnableDesc') }}
              </div>
            </div>
            <v-switch v-model="config.enabled" color="primary" density="compact" hide-details inset />
          </div>

          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalModel') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaEvalModelDesc') }}
              </div>
            </div>
            <v-autocomplete
              v-model="config.modelId"
              :items="modelItems"
              item-title="title"
              item-value="value"
              :loading="modelsLoading"
              :error-messages="modelsError"
              density="compact"
              variant="outlined"
              hide-details="auto"
              clearable
              auto-select-first
              prepend-inner-icon="mdi-robot"
              :placeholder="$t('qaEvalModelDefault')"
              style="max-width: 320px"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
              </template>
            </v-autocomplete>
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaNbaMaxPerRun') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaNbaMaxPerRunDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.maxPerRun" type="number" min="1" max="5" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>

          <!-- ⚠️ คนละแกนกับ "จำนวนใบต่อครั้ง" ข้างบน และเป็นเพดานค่าใช้จ่ายตัวเดียวที่ผูกกับ
               "1 บทสนทนา" — ถ้าไม่มีค่านี้ ทุกครั้งที่ลูกค้าพิมพ์คือ 1 LLM call ไม่มีที่สิ้นสุด -->
          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaNbaMaxPerCase') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaNbaMaxPerCaseDesc') }}
              </div>
            </div>
            <div style="max-width: 320px">
              <v-text-field v-model.number="config.maxRunsPerCase" type="number" min="1" max="200" density="compact" variant="outlined" hide-details prepend-inner-icon="mdi-repeat-off" style="max-width: 120px" />
              <div class="text-caption text-medium-emphasis mt-1">{{ runsPerCaseText }}</div>
            </div>
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalTimeout') }}</div>
            </div>
            <v-text-field v-model.number="config.timeoutSeconds" type="number" min="5" max="120" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
        </div>
      </v-card>

      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalInputTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaNbaTailLines') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaNbaTailLinesDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.maxTranscriptLines" type="number" min="2" max="200" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalMaxLineLen') }}</div>
            </div>
            <v-text-field v-model.number="config.maxCharsPerLine" type="number" min="50" max="4000" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <v-alert type="info" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaNbaPrivacyNote') }}
            </span>
          </v-alert>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaSetSaveConfig') }}</v-btn>
      <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
        {{ $t('qaSumLastEdited', { when: updatedAtText }) }}<template v-if="updatedBy"> {{ $t('qaSumLastEditedBy', { who: updatedBy }) }}</template>
      </span>
      <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSetNeverSaved') }}</span>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Q6 — แท็บตั้งค่า Next best action
 *
 * ⚠️ แท็บเดียวในหน้า QA Settings ที่บันทึกลงฐานข้อมูลจริง (cc.QaSettings)
 *    แท็บอื่นยังเก็บใน localStorage ของเบราว์เซอร์
 *
 * ⚠️ รายการโมเดลเป็น "บอทจากหน้า Model Creator" (endpoint เดิมของ framework
 *    `ManageGPTModel/DDGPTModel_IsPublishByUser` ตัวเดียวกับที่หน้าอื่นใช้)
 *    ห้ามสร้างรายการโมเดลชุดที่สองขึ้นมาเอง ไม่งั้นจะมีที่ตั้งค่าโมเดลสองที่ที่ไม่ตรงกัน
 *
 * ⚠️ เก็บเป็น **ModelID ของบอท** ไม่ใช่ GroupModelID — การเลือกกลุ่มแล้วให้ระบบหยิบบอท
 *    ที่ publish ตัวแรกในกลุ่ม แปลว่าถ้ากลุ่มมีหลายบอท ผลจะเปลี่ยนไปเองตามลำดับที่คนอื่น
 *    เผลอ publish ทีหลัง โดยไม่มีใครแตะหน้านี้เลย
 */
import qaConsole from '@/services/qaConsole';

/**
 * ตัวเลือกตั้งต้น — ปล่อยให้ระบบใช้โมเดลของ OA ที่เคสนั้นสังกัด
 * (พฤติกรรมเดิมของ social pipeline ทั้งเส้น · ดู ISocialLlmBAL ข้อตกลงข้อ 8)
 */

export default {
  name: 'QaNextBestActionSettings',
  emits: ['saved'],
  data() {
    return {
      config: {
        enabled: true,
        modelId: null,
        systemPrompt: '',
        maxPerRun: 2,
        maxRunsPerCase: 10,
        timeoutSeconds: 20,
        maxTranscriptLines: 30,
        maxCharsPerLine: 500,
      },
      updatedAt: null,
      updatedBy: null,
      saving: false,
      modelsLoading: false,
      modelsError: '',
      modelItems: [],
    };
  },
  computed: {
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
    /** สื่อเพดานเป็นภาษาคน — "10 ครั้ง" ไม่ได้บอกอะไรถ้าไม่รู้ว่า 1 ครั้ง = ลูกค้าพิมพ์ 1 รอบ */
    runsPerCaseText() {
      const n = Number(this.config.maxRunsPerCase);
      if (!Number.isFinite(n) || n < 1) return '';
      return this.$t('qaNbaCapText', { calls: n, cards: n * Math.max(1, Number(this.config.maxPerRun) || 1) });
    },
  },
  mounted() {
    this.load();
    this.loadModels();
  },
  methods: {
    /** หัวรายการ dropdown เลือกโมเดล — เมธอด ไม่ใช่ const ระดับโมดูล (ดู QaEvaluatorSettings) */
    defaultModelItem() {
      return { title: this.$t('qaEvalModelDefaultTitle'), subtitle: this.$t('qaEvalModelDefaultSub'), value: null };
    },
    async load() {
      try {
        const res = await qaConsole.getNbaSettings();
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaEvalLoadFailed', { msg: err.message }) });
      }
    },

    /**
     * รายการบอทจากหน้า Model Creator — endpoint เดียวกับที่หน้าอื่นของระบบใช้
     * (`api/v0/ManageGPTModel/DDGPTModel_IsPublishByUser` · กรอง ActiveFlag + IsPublish
     *  และ scope ตาม role/company ให้แล้วฝั่ง server)
     *
     * ⚠️ ต้องเลือก "บอท" ไม่ใช่ "กลุ่มโมเดล" — การเลือกกลุ่มแล้วให้ระบบหยิบบอทที่ publish
     *    ตัวแรกในกลุ่มนั้น แปลว่าถ้ากลุ่มมีหลายบอท ผลจะเปลี่ยนไปเองตามลำดับที่คนอื่น
     *    เผลอ publish ทีหลัง โดยไม่มีใครแตะหน้านี้เลย
     *
     * ⚠️ ต้องมีตัวเลือก "ตามค่าของช่องทาง" (null) อยู่หัวรายการเสมอ — เป็นพฤติกรรมตั้งต้น
     *    ของ pipeline ทั้งเส้น (เลือกโมเดลจาก GroupModelID ของ OA) การบังคับให้เลือกบอท
     *    ตายตัวจะทำให้ทุก OA ใช้บอทเดียวกันโดยไม่ได้ตั้งใจ
     */
    loadModels() {
      const self = this;
      if (!this.$call || typeof this.$call.DDGPTModel_IsPublishByUser !== 'function') return;

      this.modelsLoading = true;
      this.$call.DDGPTModel_IsPublishByUser(
        (dataResponse) => {
          self.modelsLoading = false;
          // response ห่อด้วย ResponseModel เหมือนทุก endpoint ของ framework (status 0 = สำเร็จ)
          // payload เป็น ResponseDropDownModel { id = ModelID, name = ModelName, TypeName = TypeLLM }
          if (!dataResponse || dataResponse.status !== 0) {
            self.modelsError = self.$t('qaEvalModelsFailed');
            return;
          }
          const rows = dataResponse.data || [];
          self.modelsError = rows.length ? '' : self.$t('qaEvalNoModels');
          self.modelItems = [self.defaultModelItem()].concat(
            rows.map((r) => ({
              title: r.name || `#${r.id}`,
              // TypeLLM = ผู้ให้บริการ/ชนิดโมเดล — ตัวเดียวกับที่หน้า Model Creator แสดง
              subtitle: [r.TypeName, `ModelID ${r.id}`].filter(Boolean).join(' · '),
              value: r.id,
            }))
          );
        },
        () => {
          self.modelsLoading = false;
          // ดึงรายการไม่ได้ก็ยังตั้งค่าอื่นได้ — เหลือตัวเลือกตั้งต้นไว้ตัวเดียว
          self.modelsError = self.$t('qaEvalModelsFailed');
        }
      );
    },

    async save() {
      this.saving = true;
      try {
        const res = await qaConsole.saveNbaSettings(this.config);
        // ⚠️ ใช้ค่าที่ server คืนกลับมา ไม่ใช่ค่าที่พิมพ์ — server บีบค่าที่เกินขอบให้อยู่ในช่วง
        //    ที่ใช้ได้ ถ้าไม่รับค่ากลับ หน้าจอจะแสดงค่าที่คนกรอกแต่ระบบใช้อีกค่าหนึ่ง
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaNbaSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ⚠️ คัดลอกมาจาก QaEvaluatorSettings.vue โดยตั้งใจ — คลาสนี้เป็น scoped style
   ของแต่ละ component ไม่ใช่ของกลาง ถ้าไม่ใส่ แถวตั้งค่าจะกองซ้อนกันเป็นแนวตั้ง */
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
/* แถวที่คำอธิบายยาว — วางช่องเลือกไว้ใต้คำอธิบายแทนที่จะบีบให้อยู่ข้าง ๆ */
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
</style>
